import { useEffect, useState } from "react";
import { useParams, useSearchParams } from "react-router-dom";
import { motion } from "framer-motion";
import Nav from "@/components/Nav";
import { publicDeck } from "@/lib/api";
import { Loader2, Lock, Download, Eye } from "lucide-react";

export default function PublicDeck() {
  const { token } = useParams();
  const [params] = useSearchParams();
  const initialPw = params.get("pw") || "";
  const [pw, setPw] = useState(initialPw);
  const [deck, setDeck] = useState(null);
  const [needsPw, setNeedsPw] = useState(false);
  const [busy, setBusy] = useState(true);
  const [err, setErr] = useState("");

  const load = async (passwordToUse) => {
    setBusy(true);
    setErr("");
    try {
      const data = await publicDeck.get(token, passwordToUse);
      if (data.needs_password) { setNeedsPw(true); setDeck(null); }
      else { setDeck(data); setNeedsPw(false); }
    } catch { setErr("Deck not found"); } finally { setBusy(false); }
  };

  useEffect(() => { load(initialPw); /* eslint-disable-next-line */ }, [token]);

  return (
    <div className="min-h-screen">
      <Nav />
      <div className="pt-10 pb-16 px-6">
        <div className="max-w-5xl mx-auto">
          {busy && (
            <div className="flex items-center gap-2 text-zinc-400"><Loader2 size={14} className="animate-spin" /> Loading…</div>
          )}
          {!busy && err && (
            <div className="kd-panel p-10 text-center">
              <h1 className="kd-display text-2xl">Deck not found.</h1>
              <p className="text-zinc-400 mt-2">The link may be invalid or has been disabled.</p>
            </div>
          )}
          {!busy && needsPw && (
            <div className="kd-panel p-10 max-w-md mx-auto">
              <div className="kd-tag mb-3">Password protected</div>
              <h1 className="kd-display text-2xl mb-2">Enter passcode.</h1>
              <p className="text-zinc-400 text-sm mb-5">This deck requires a password to view.</p>
              <div className="relative">
                <Lock size={14} className="absolute left-4 top-1/2 -translate-y-1/2 text-zinc-500" />
                <input
                  data-testid="public-deck-password"
                  type="password"
                  value={pw}
                  onChange={(e) => setPw(e.target.value)}
                  className="w-full bg-black/40 border border-white/10 rounded-xl pl-11 pr-4 py-3.5 text-white text-sm outline-none focus:border-white/30"
                  placeholder="Password"
                />
              </div>
              <button
                data-testid="public-deck-unlock"
                onClick={() => load(pw)}
                className="mt-4 w-full rounded-full px-6 py-3 bg-[#E50914] hover:bg-[#FF2A2D] text-white transition-all duration-300 hover:shadow-[0_0_28px_rgba(229,9,20,0.5)]"
              >
                Unlock
              </button>
            </div>
          )}
          {!busy && deck && (
            <PublicDeckView deck={deck} token={token} pw={pw} />
          )}
        </div>
      </div>
    </div>
  );
}

function PublicDeckView({ deck, token, pw }) {
  const c = deck.concept || {};
  const palette = c.palette || ["#0A0A0A", "#E50914", "#FFFFFF"];
  return (
    <div>
      <div className="flex items-center justify-between mb-6 flex-wrap gap-3">
        <div className="flex items-center gap-3 text-zinc-500 text-xs">
          <Eye size={14} /> {deck.views} view{deck.views === 1 ? "" : "s"}
        </div>
        {deck.has_pdf && (
          <a
            data-testid="public-deck-download"
            href={publicDeck.pdfUrl(token, pw)}
            target="_blank" rel="noreferrer"
            className="rounded-full px-5 py-2.5 bg-[#E50914] hover:bg-[#FF2A2D] text-white text-sm transition-all duration-300 hover:shadow-[0_0_24px_rgba(229,9,20,0.4)] flex items-center gap-2"
          >
            <Download size={14} /> Download PDF
          </a>
        )}
      </div>

      {/* Cover */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.7 }}
        className="rounded-3xl overflow-hidden mb-6 aspect-[16/9] relative"
        style={{ background: palette[0] }}
      >
        <div
          className="absolute inset-0"
          style={{ background: `radial-gradient(circle at 85% 15%, ${palette[1]}44, transparent 55%)` }}
        />
        {deck.main_image && (
          <img
            src={deck.main_image}
            alt=""
            className="absolute right-0 top-0 h-full w-1/2 object-cover"
            style={{ maskImage: "linear-gradient(90deg, transparent 0%, black 30%)" }}
          />
        )}
        <div className="absolute inset-0 p-8 md:p-12 flex flex-col justify-between">
          <div className="text-[10px] tracking-[0.25em] uppercase text-white/60">Electronic Press Kit</div>
          <div>
            <div className="text-white kd-display leading-[0.9] tracking-tight text-5xl md:text-7xl">{deck.stage_name}</div>
            <div className="mt-3 text-white/60 text-sm tracking-[0.2em] uppercase">{deck.creator_type}</div>
          </div>
        </div>
      </motion.div>

      {/* Pages grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        {(deck.pages || []).slice(1).map((p, i) => (
          <motion.div
            key={i}
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: "-40px" }}
            transition={{ duration: 0.5, delay: i * 0.05 }}
            data-testid={`public-page-${i}`}
            className="rounded-3xl p-8 min-h-[280px]"
            style={{ background: palette[0], border: `1px solid ${palette[1]}22` }}
          >
            <div className="text-white/50 text-[10px] tracking-[0.25em] uppercase mb-3">{p.module}</div>
            <div className="kd-display text-2xl md:text-3xl text-white mb-4 leading-tight">{p.title}</div>
            <p className="text-zinc-300 text-sm leading-relaxed whitespace-pre-wrap">
              {p.content?.slice(0, 700) || "—"}
            </p>
          </motion.div>
        ))}
      </div>

      {/* Socials */}
      {deck.socials && deck.socials.length > 0 && (
        <div className="mt-8 kd-panel p-8">
          <div className="text-zinc-500 text-[11px] tracking-[0.2em] uppercase mb-4">Follow</div>
          <div className="flex flex-wrap gap-3">
            {deck.socials.map((s, i) => (
              <a
                key={i}
                href={s.url}
                target="_blank" rel="noreferrer"
                className="rounded-full px-4 py-2 bg-white/[0.03] border border-white/[0.08] text-white text-xs hover:bg-white/[0.06] transition-colors"
              >
                {s.platform} {s.followers ? `· ${s.followers}` : ""}
              </a>
            ))}
          </div>
        </div>
      )}

      <div className="mt-10 text-center text-zinc-600 text-xs">
        Made with <a href="/" className="text-white hover:text-[#FF2A2D] transition-colors">KlickDeck</a>
      </div>
    </div>
  );
}
